<script lang="ts" setup>
import { useRouter } from "vue-router";
import { IonHeader, IonToolbar, RefresherEventDetail } from "@ionic/vue";
import { useFabriqStore } from "@/store/fabriq";
import { storeToRefs } from "pinia";
import UserAvatar from "@/components/business/UserAvatar.vue";
import ConnectivityInfo from "@/components/ui/ConnectivityInfo.vue";
import { nextTick, ref } from "vue";
import { useConnectivityInfo } from "@/composables/useConnectivityInfo";

defineProps({
  title: {
    type: String,
    required: true,
  },
  collapse: {
    type: Boolean,
    default: false,
  },
  isAccount: {
    type: Boolean,
    default: false,
  },
  beta: {
    type: Boolean,
    default: false,
  },
  showPageRefreshButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (event: "refresh", payload: RefresherEventDetail): void;
}>();

const router = useRouter();
const fabriqStore = useFabriqStore();
const { user } = storeToRefs(fabriqStore);
fabriqStore.setLoading(false);

const { online } = useConnectivityInfo();

const gotoAccount = () => {
  router.push("/account");
};

const isLoading = ref(false);

function refreshPage() {
  emit("refresh", {
    async complete() {
      await nextTick(); // Wait until next tick in case the complete method is called synchronously
      isLoading.value = false;
    },
  });
  isLoading.value = true;
}
</script>

<template>
  <ion-header
    mode="ios"
    class="ion-no-border"
    :collapse="collapse ? 'condense' : undefined"
    :translucent="!collapse"
  >
    <ion-toolbar v-if="collapse" mode="ios">
      <div class="page-toolbar">
        <div class="page-title-container">
          <div class="page-title">
            {{ title }}
          </div>

          <font-icon
            v-if="online && showPageRefreshButton && !isLoading"
            class="page-title-refresh"
            color="var(--ion-color-secondary)"
            name="rotate-right"
            size="1.4"
            @click="refreshPage"
          />

          <font-icon
            v-if="isLoading"
            class="fa-solid page-title-refresh page-title-refresh-spinner"
            color="var(--ion-color-secondary)"
            name="circle-notch"
            size="1.3"
            :spin="isLoading"
            @click="refreshPage"
          />
          <span class="beta" v-if="beta">BETA</span>
        </div>

        <connectivity-info />
        <user-avatar
          slot="end"
          class="page-toolbar-avatar"
          :large="true"
          clickable
          v-if="collapse && !isAccount"
          :user="user"
          @click="gotoAccount"
        />
      </div>
      <slot v-if="collapse" />
    </ion-toolbar>
    <div v-else class="fake-toolbar">&nbsp;</div>
  </ion-header>
</template>

<style scoped>
.spinner {
  margin-right: 1rem;
}

.page-toolbar-avatar {
  --avatar-size: 36px;
}
.page-title-container {
  display: flex;
  gap: 0.75rem;
}

.page-title-refresh {
  margin-top: 0.4rem;
}

.page-title-refresh-spinner {
  transform: rotate(90deg);
  --fa-animation-duration: 1s;
}

.page-title {
  flex: 1;
  font-size: var(--font-size-xl);
  font-weight: bold;
  display: flex;
  align-items: center;
}

.fake-toolbar {
  --_fake-toolbar-height: var(--fake-toolbar-height, 48px);
  min-height: var(--_fake-toolbar-height);
  max-height: var(--_fake-toolbar-height);
  height: var(--_fake-toolbar-height);
}

.beta {
  font-size: var(--font-size-xs);
  color: var(--ion-color-primary);
  opacity: 0.5;
  padding: 4px;
  position: relative;
  align-self: center;
  margin-top: 0.5rem;
  display: none;
}

.beta::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--ion-color-primary);
  opacity: 0.1;
  border-radius: 4px;
}
</style>
